import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Trash2, ArrowUp, ArrowDown, Plus, Upload, ImageIcon, RefreshCw, BarChart3 } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";
import { SchedulingSettings } from "../components/SchedulingSettings";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (image) => (!image ? "" : image.startsWith("http") ? image : `${BACKEND}${image}`);

const DishCard = ({ item, first, last, busy, categories, onSave, onDelete, onMove, onUpload }) => {
  const [draft, setDraft] = useState(item);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setDraft(item); }, [item]);

  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const catLabel = (categories.find((c) => c.key === (draft.category || "dinner")) || {}).label || draft.category;

  const images = draft.images && draft.images.length ? draft.images : (draft.image ? [draft.image] : []);
  const setImages = (arr) => setDraft((p) => ({ ...p, images: arr, image: arr[0] || "" }));

  const addImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const url = await onUpload(file);
    if (url) setImages([...images, url].slice(0, 3));
    setUploading(false);
  };
  const replaceImage = async (idx, file) => {
    if (!file) return;
    setUploading(true);
    const url = await onUpload(file);
    if (url) setImages(images.map((im, i) => (i === idx ? url : im)));
    setUploading(false);
  };
  const removeImage = (idx) => setImages(images.filter((_, i) => i !== idx));

  return (
    <div className="border-2 border-[#E8F5E9] bg-white p-5" data-testid={`dish-row-${item.id}`}>
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="w-full sm:w-44 flex-shrink-0">
          <div className="grid grid-cols-3 sm:grid-cols-2 gap-2">
            {images.map((im, idx) => (
              <div key={idx} className="relative aspect-square bg-[#E8F5E9] overflow-hidden group" data-testid={`dish-photo-${item.id}-${idx}`}>
                <img src={imgSrc(im)} alt={`${draft.name} ${idx + 1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(idx)} data-testid={`dish-photo-remove-${item.id}-${idx}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#B71C1C] text-white flex items-center justify-center opacity-90 hover:opacity-100" aria-label="Foto verwijderen">
                  <Trash2 size={13} />
                </button>
                <label className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-white/90 text-[#0D3B4C] flex items-center justify-center cursor-pointer hover:bg-white" aria-label="Foto vervangen">
                  <Upload size={12} />
                  <input type="file" accept="image/*" className="hidden" data-testid={`dish-photo-replace-${item.id}-${idx}`} onChange={(e) => replaceImage(idx, e.target.files?.[0])} />
                </label>
              </div>
            ))}
            {images.length < 3 && (
              <label className="aspect-square bg-[#E8F5E9] flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-[#d4ecd7] transition-colors text-[#1B6B2E]" data-testid={`dish-photo-add-${item.id}`}>
                {uploading ? <span className="text-xs font-[700]">...</span> : <><Plus size={18} /><span className="text-[10px] font-[700]">Foto</span></>}
                <input type="file" accept="image/*" className="hidden" data-testid={`dish-image-upload-${item.id}`} onChange={(e) => addImage(e.target.files?.[0])} />
              </label>
            )}
            {images.length === 0 && (
              <div className="aspect-square bg-[#E8F5E9] flex items-center justify-center col-span-2"><ImageIcon size={26} className="text-[#1B6B2E]" /></div>
            )}
          </div>
          <p className="mt-1 text-[10px] text-[#0D3B4C]/70">Tot 3 foto's</p>
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2" data-testid={`dish-badges-${item.id}`}>
            <span className="inline-flex items-center px-2.5 py-1 text-xs font-[700] uppercase tracking-wide bg-[#7A5230] text-white rounded-full" data-testid={`dish-cat-badge-${item.id}`}>
              {catLabel}
            </span>
            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-[700] uppercase tracking-wide rounded-full ${draft.active ? "bg-[#E8F5E9] text-[#1B6B2E]" : "bg-[#F1F1F1] text-[#9AA0A6]"}`}>
              {draft.active ? "Actief" : "Verborgen"}
            </span>
            {(draft.sold_out || item.out_of_stock) && (
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-[700] uppercase tracking-wide bg-[#B71C1C] text-white rounded-full" data-testid={`dish-status-soldout-${item.id}`}>
                Uitverkocht
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Naam</label>
              <input value={draft.name} onChange={(e) => set("name", e.target.value)} data-testid={`dish-name-${item.id}`}
                className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Prijs (€)</label>
              <input type="number" step="0.01" min="0" value={draft.price} onChange={(e) => set("price", e.target.value)} data-testid={`dish-price-${item.id}`}
                className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Beschrijving</label>
            <textarea value={draft.desc} onChange={(e) => set("desc", e.target.value)} data-testid={`dish-desc-${item.id}`} rows={2}
              className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[400] focus:border-[#B71C1C] focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Categorie</label>
              <select value={draft.category || "dinner"} onChange={(e) => set("category", e.target.value)} data-testid={`dish-category-${item.id}`}
                className="w-full border-2 border-[#7A5230] px-3 py-2 font-[700] text-[#7A5230] bg-white focus:border-[#B71C1C] focus:outline-none">
                {categories.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Beschikbaar vandaag</label>
              <input type="number" min="0" value={draft.availability} onChange={(e) => set("availability", e.target.value)} data-testid={`dish-availability-${item.id}`}
                className="w-full border-2 border-[#1B6B2E] px-3 py-2 font-[700] text-[#1B6B2E] focus:border-[#B71C1C] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Standaard/dag</label>
              <input type="number" min="0" value={draft.default_availability} onChange={(e) => set("default_availability", e.target.value)} data-testid={`dish-default-avail-${item.id}`}
                className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
            </div>
            <div className="flex flex-col">
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Actief (zichtbaar)</label>
              <button type="button" role="switch" aria-checked={!!draft.active}
                onClick={() => set("active", !draft.active)} data-testid={`dish-active-${item.id}`}
                className={`relative w-14 h-7 rounded-full transition-colors ${draft.active ? "bg-[#1B6B2E]" : "bg-[#cbd5cb]"}`}>
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${draft.active ? "translate-x-7" : "translate-x-0"}`} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <span className="text-xs font-[600] text-[#0D3B4C]">Markeer als uitverkocht</span>
            <button type="button" role="switch" aria-checked={!!draft.sold_out}
              onClick={() => set("sold_out", !draft.sold_out)} data-testid={`dish-soldout-toggle-${item.id}`}
              className={`relative w-14 h-7 rounded-full transition-colors ${draft.sold_out ? "bg-[#B71C1C]" : "bg-[#cbd5cb]"}`}>
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${draft.sold_out ? "translate-x-7" : "translate-x-0"}`} />
            </button>
            {item.out_of_stock && !draft.sold_out && (
              <span className="text-sm font-[700] text-[#B71C1C]" data-testid={`dish-outofstock-${item.id}`}>Voorraad op (0 beschikbaar)</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button onClick={() => onSave(item.id, draft)} disabled={busy} data-testid={`dish-save-${item.id}`}
              className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] px-4 py-2 hover:bg-[#144F22] transition-colors disabled:opacity-40">
              <Save size={16} /> Opslaan
            </button>
            <button onClick={() => onMove(item.id, -1)} disabled={busy || first} data-testid={`dish-up-${item.id}`}
              className="flex items-center gap-1 bg-[#E8F5E9] text-[#0D3B4C] font-[700] px-3 py-2 hover:bg-[#d4ecd7] transition-colors disabled:opacity-30" aria-label="Omhoog">
              <ArrowUp size={16} />
            </button>
            <button onClick={() => onMove(item.id, 1)} disabled={busy || last} data-testid={`dish-down-${item.id}`}
              className="flex items-center gap-1 bg-[#E8F5E9] text-[#0D3B4C] font-[700] px-3 py-2 hover:bg-[#d4ecd7] transition-colors disabled:opacity-30" aria-label="Omlaag">
              <ArrowDown size={16} />
            </button>
            <button onClick={() => onDelete(item.id, item.name)} disabled={busy} data-testid={`dish-delete-${item.id}`}
              className="flex items-center gap-2 bg-[#B71C1C] text-white font-[700] px-4 py-2 hover:bg-[#8E1515] transition-colors disabled:opacity-40 ml-auto">
              <Trash2 size={16} /> Verwijderen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WEEKDAYS_NL = [["0", "Ma"], ["1", "Di"], ["2", "Wo"], ["3", "Do"], ["4", "Vr"], ["5", "Za"], ["6", "Zo"]];

const CategoryManager = ({ categories, busy, onSave, onUpload }) => {
  const [draft, setDraft] = useState(categories);
  useEffect(() => { setDraft(categories); }, [categories]);

  const set = (key, k, v) => setDraft((p) => p.map((c) => (c.key === key ? { ...c, [k]: v } : c)));
  const move = (idx, dir) => setDraft((p) => {
    const arr = [...p];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return arr;
  });
  const toggleDay = (key, dayNum) => setDraft((p) => p.map((c) => {
    if (c.key !== key) return c;
    const cur = new Set((c.pickup_weekdays || []).map(Number));
    cur.has(dayNum) ? cur.delete(dayNum) : cur.add(dayNum);
    return { ...c, pickup_weekdays: [...cur] };
  }));
  const addSlot = (key) => set(key, "slots", [...(draft.find((c) => c.key === key)?.slots || []), { start: "12:00", end: "14:00" }]);
  const setSlot = (key, i, field, v) => setDraft((p) => p.map((c) => c.key === key ? { ...c, slots: c.slots.map((s, si) => si === i ? { ...s, [field]: v } : s) } : c));
  const removeSlot = (key, i) => setDraft((p) => p.map((c) => c.key === key ? { ...c, slots: c.slots.filter((_, si) => si !== i) } : c));

  const handleUpload = async (key, file) => {
    if (!file) return;
    const url = await onUpload(file);
    if (url) set(key, "image", url);
  };

  const save = () => onSave(draft.map((c, i) => ({ ...c, order: i })));

  return (
    <div className="bg-[#FBF3E7] border-2 border-[#7A5230] p-5" data-testid="category-manager">
      <h2 className="font-[700] text-[#7A5230] mb-1">Categorieën beheren</h2>
      <p className="text-sm text-[#0D3B4C] mb-4">
        Sleep-volgorde (pijltjes), hernoemen, in/uitschakelen, uitverkocht, kopfoto, weergavelimiet en ophaalschema per categorie.
      </p>
      <div className="space-y-4">
        {draft.map((c, idx) => (
          <div key={c.key} className="bg-white border-2 border-[#E8D9C3] p-4 space-y-3" data-testid={`category-row-${c.key}`}>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex flex-col items-center">
                <span className="text-xs font-[600] text-[#0D3B4C] mb-1">Volgorde</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`category-up-${c.key}`}
                    className="bg-[#E8F5E9] text-[#0D3B4C] p-2 disabled:opacity-30" aria-label="Omhoog"><ArrowUp size={16} /></button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === draft.length - 1} data-testid={`category-down-${c.key}`}
                    className="bg-[#E8F5E9] text-[#0D3B4C] p-2 disabled:opacity-30" aria-label="Omlaag"><ArrowDown size={16} /></button>
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Weergavenaam ({c.key})</label>
                <input value={c.label} onChange={(e) => set(c.key, "label", e.target.value)} data-testid={`category-label-${c.key}`}
                  className="w-full border-2 border-[#7A5230] px-3 py-2 font-[700] text-[#7A5230] focus:border-[#B71C1C] focus:outline-none" />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-xs font-[600] text-[#0D3B4C] mb-1">Actief</span>
                <button type="button" role="switch" aria-checked={c.enabled} onClick={() => set(c.key, "enabled", !c.enabled)} data-testid={`category-enabled-${c.key}`}
                  className={`relative w-14 h-7 rounded-full transition-colors ${c.enabled ? "bg-[#1B6B2E]" : "bg-[#cbd5cb]"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${c.enabled ? "translate-x-7" : "translate-x-0"}`} />
                </button>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-xs font-[600] text-[#0D3B4C] mb-1">Uitverkocht</span>
                <button type="button" role="switch" aria-checked={c.sold_out} onClick={() => set(c.key, "sold_out", !c.sold_out)} data-testid={`category-soldout-${c.key}`}
                  className={`relative w-14 h-7 rounded-full transition-colors ${c.sold_out ? "bg-[#B71C1C]" : "bg-[#cbd5cb]"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${c.sold_out ? "translate-x-7" : "translate-x-0"}`} />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Kopfoto (banner)</label>
                <div className="flex items-center gap-3">
                  {c.image ? <img src={imgSrc(c.image)} alt={c.label} className="w-20 h-12 object-cover rounded" /> : <div className="w-20 h-12 bg-[#E8F5E9] rounded flex items-center justify-center"><ImageIcon size={18} className="text-[#1B6B2E]" /></div>}
                  <label className="flex items-center gap-2 bg-[#E8F5E9] text-[#0D3B4C] font-[700] text-sm py-2 px-3 cursor-pointer hover:bg-[#d4ecd7]">
                    <Upload size={14} /> Foto kiezen
                    <input type="file" accept="image/*" className="hidden" data-testid={`category-image-${c.key}`} onChange={(e) => handleUpload(c.key, e.target.files?.[0])} />
                  </label>
                  {c.image && <button type="button" onClick={() => set(c.key, "image", "")} className="text-[#B71C1C] text-sm font-[700]" data-testid={`category-image-clear-${c.key}`}>Verwijder</button>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Max. getoond (0 = alle)</label>
                <input type="number" min="0" value={c.display_limit || 0} onChange={(e) => set(c.key, "display_limit", parseInt(e.target.value, 10) || 0)} data-testid={`category-limit-${c.key}`}
                  className="w-28 border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Min. bestelling (0 = geen)</label>
                <input type="number" min="0" value={c.min_order || 0} onChange={(e) => set(c.key, "min_order", parseInt(e.target.value, 10) || 0)} data-testid={`category-minorder-${c.key}`}
                  className="w-28 border-2 border-[#7A5230] px-3 py-2 font-[700] text-[#7A5230] focus:border-[#B71C1C] focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Beschrijving (introtekst onder de kop)</label>
              <textarea value={c.description || ""} onChange={(e) => set(c.key, "description", e.target.value)} data-testid={`category-description-${c.key}`} rows={2}
                placeholder="Korte sfeertekst of uitleg voor deze categorie…"
                className="w-full border-2 border-[#7A5230] px-3 py-2 font-[400] text-[#0D3B4C] focus:border-[#B71C1C] focus:outline-none" />
            </div>

            <div className="border-t border-[#E8D9C3] pt-3">
              <label className="block text-xs font-[700] text-[#7A5230] mb-2">Ophaalschema (leeg = volgt globale instelling)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {WEEKDAYS_NL.map(([num, lbl]) => {
                  const active = (c.pickup_weekdays || []).map(Number).includes(Number(num));
                  return (
                    <button key={num} type="button" onClick={() => toggleDay(c.key, Number(num))} data-testid={`category-day-${c.key}-${num}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-[700] border-2 transition-colors ${active ? "bg-[#7A5230] text-white border-[#7A5230]" : "bg-white text-[#7A5230] border-[#E8D9C3]"}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
              <div className="space-y-2">
                {(c.slots || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`category-slot-${c.key}-${i}`}>
                    <input type="time" value={s.start} onChange={(e) => setSlot(c.key, i, "start", e.target.value)} className="border-2 border-[#7A5230] px-2 py-1.5 font-[600] text-[#7A5230]" />
                    <span className="font-[700] text-[#7A5230]">–</span>
                    <input type="time" value={s.end} onChange={(e) => setSlot(c.key, i, "end", e.target.value)} className="border-2 border-[#7A5230] px-2 py-1.5 font-[600] text-[#7A5230]" />
                    <button type="button" onClick={() => removeSlot(c.key, i)} data-testid={`category-slot-remove-${c.key}-${i}`} className="text-[#B71C1C]" aria-label="Verwijder tijdslot"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => addSlot(c.key)} data-testid={`category-add-slot-${c.key}`}
                  className="flex items-center gap-1 text-[#7A5230] font-[700] text-sm"><Plus size={15} /> Tijdslot toevoegen</button>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 mt-3 pt-3 border-t border-dashed border-[#E8D9C3]">
                <div>
                  <label className="block text-xs font-[700] text-[#7A5230] mb-1">Bestel-deadline (uur)</label>
                  <input type="time" value={c.order_deadline || ""} onChange={(e) => set(c.key, "order_deadline", e.target.value)} data-testid={`category-deadline-${c.key}`}
                    className="border-2 border-[#7A5230] px-2 py-1.5 font-[600] text-[#7A5230]" />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-[600] text-[#0D3B4C]">{c.same_day ? "Afhalen: zelfde dag" : "Afhalen: volgende dag"}</span>
                  <button type="button" role="switch" aria-checked={!!c.same_day} onClick={() => set(c.key, "same_day", !c.same_day)} data-testid={`category-sameday-${c.key}`}
                    className={`relative w-14 h-7 rounded-full transition-colors ${c.same_day ? "bg-[#1B6B2E]" : "bg-[#cbd5cb]"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${c.same_day ? "translate-x-7" : "translate-x-0"}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="save-categories"
        className="flex items-center gap-2 mt-4 bg-[#7A5230] text-white font-[700] px-5 py-2 hover:bg-[#5f3f24] transition-colors disabled:opacity-40">
        <Save size={16} /> Categorieën opslaan
      </button>
    </div>
  );
};

export const MenuManagement = ({ onAuthError, onSettingsChange }) => {
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [displayCount, setDisplayCount] = useState("");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState({ name: "", price: "", desc: "", default_availability: 50, image: "", category: "dinner" });

  const err = useCallback((e) => {
    if (e?.response?.status === 401) onAuthError?.();
    else toast.error(e?.response?.data?.detail || "Er ging iets mis.");
  }, [onAuthError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, ov] = await Promise.all([
        axios.get(`${API}/admin/menu`, { headers: authHeader() }),
        axios.get(`${API}/admin/settings`, { headers: authHeader() }),
        axios.get(`${API}/admin/stock-overview`, { headers: authHeader() }),
      ]);
      setItems(m.data.map((d) => ({ ...d, active: d.is_active })));
      setSettings(s.data);
      setCategories(s.data.categories || []);
      setDisplayCount(String(s.data.display_count));
      setOverview(ov.data);
      onSettingsChange?.(s.data);
    } catch (e) { err(e); } finally { setLoading(false); }
  }, [authHeader, err, onSettingsChange]);

  useEffect(() => { load(); }, [load]);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/menu/upload`, fd, { headers: { ...authHeader() } });
      return data.url;
    } catch (e) { err(e); return ""; }
  };

  const saveDish = async (id, draft) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/menu/${id}`, {
        name: draft.name,
        price: parseFloat(draft.price) || 0,
        desc: draft.desc,
        image: draft.image,
        images: draft.images && draft.images.length ? draft.images : (draft.image ? [draft.image] : []),
        availability: parseInt(draft.availability, 10) || 0,
        default_availability: parseInt(draft.default_availability, 10) || 0,
        is_active: !!draft.active,
        category: draft.category || "dinner",
        sold_out: !!draft.sold_out,
      }, { headers: authHeader() });
      toast.success("Gerecht opgeslagen.");
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const deleteDish = async (id, name) => {
    if (!window.confirm(`"${name}" definitief verwijderen?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/menu/${id}`, { headers: authHeader() });
      toast.success("Gerecht verwijderd.");
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const moveDish = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const a = items[idx], b = items[swap];
    setBusy(true);
    try {
      await Promise.all([
        axios.patch(`${API}/admin/menu/${a.id}`, { sort_order: b.sort_order }, { headers: authHeader() }),
        axios.patch(`${API}/admin/menu/${b.id}`, { sort_order: a.sort_order }, { headers: authHeader() }),
      ]);
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const saveCategories = async (draft) => {
    setBusy(true);
    try {
      const payload = {};
      draft.forEach((c) => {
        payload[c.key] = {
          label: c.label, enabled: c.enabled, sold_out: c.sold_out,
          image: c.image || "", display_limit: c.display_limit || 0, order: c.order, min_order: c.min_order || 0,
          description: c.description || "",
          order_deadline: c.order_deadline || "", same_day: !!c.same_day,
          pickup_weekdays: (c.pickup_weekdays || []).map(Number),
          slots: (c.slots || []).map((s) => ({ start: s.start, end: s.end })),
        };
      });
      const { data } = await axios.patch(`${API}/admin/settings`, { categories: payload }, { headers: authHeader() });
      setSettings(data);
      setCategories(data.categories || []);
      onSettingsChange?.(data);
      toast.success("Categorieën opgeslagen.");
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const saveDisplayCount = async () => {
    const n = parseInt(displayCount, 10);
    if (!n || n < 1) { toast.error("Aantal moet minimaal 1 zijn."); return; }
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/settings`, { display_count: n }, { headers: authHeader() });
      setSettings(data);
      onSettingsChange?.(data);
      toast.success("Aantal getoonde gerechten opgeslagen.");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const addDish = async () => {
    if (!adding.name.trim()) { toast.error("Vul een naam in."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/menu`, {
        name: adding.name.trim(),
        price: parseFloat(adding.price) || 0,
        desc: adding.desc,
        image: adding.image,
        default_availability: parseInt(adding.default_availability, 10) || 50,
        category: adding.category || "dinner",
      }, { headers: authHeader() });
      toast.success("Gerecht toegevoegd.");
      setAdding({ name: "", price: "", desc: "", default_availability: 50, image: "", category: "dinner" });
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const resetAvailability = async () => {
    if (!window.confirm("Alle gerechten op hun dagelijkse standaard zetten? Dit overschrijft de huidige beschikbaarheid van vandaag.")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/menu/reset-availability`, {}, { headers: authHeader() });
      toast.success(`Klaar voor vandaag — ${data.count} gerechten op dagstandaard gezet.`);
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  if (loading) return <p className="font-[600] text-[#0D3B4C]">Laden...</p>;

  const activeCount = items.filter((i) => i.active).length;

  return (
    <div data-testid="menu-mgmt" className="space-y-8">
      {/* Daily quick-start: reset all to default */}
      <div className="bg-[#E8F5E9] border-2 border-[#1B6B2E] p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="font-[700] text-[#0D3B4C]">Klaar voor vandaag</h2>
          <p className="text-sm text-[#0D3B4C]">
            Zet in één tik alle gerechten op hun dagelijkse standaard-voorraad. Handig als startpunt 's ochtends.
          </p>
        </div>
        <button onClick={resetAvailability} disabled={busy} data-testid="reset-availability"
          className="flex items-center justify-center gap-2 bg-[#1B6B2E] text-white font-[700] uppercase tracking-wider px-6 py-3 hover:bg-[#144F22] transition-colors disabled:opacity-40 whitespace-nowrap">
          <RefreshCw size={18} /> Zet alles op dagstandaard
        </button>
      </div>

      {/* Display count setting */}
      <div className="bg-[#FEF9E7] p-5">
        <h2 className="font-[700] text-[#0D3B4C] mb-1">Weergave op menupagina</h2>
        <p className="text-sm text-[#0D3B4C] mb-4">
          Kies hoeveel gerechten er (in volgorde) op de menupagina worden getoond. Je hebt nu {activeCount} actieve gerechten.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Aantal getoonde gerechten</label>
            <input type="number" min="1" value={displayCount} onChange={(e) => setDisplayCount(e.target.value)} data-testid="display-count-input"
              className="w-32 border-2 border-[#0D3B4C] px-3 py-2 font-[700] focus:border-[#B71C1C] focus:outline-none" />
          </div>
          <button onClick={saveDisplayCount} disabled={busy} data-testid="save-display-count"
            className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] px-5 py-2 hover:bg-[#144F22] transition-colors disabled:opacity-40">
            <Save size={16} /> Opslaan
          </button>
        </div>
      </div>

      {/* Scheduling & availability controls */}
      <SchedulingSettings
        settings={settings}
        onSaved={(data) => { setSettings(data); onSettingsChange?.(data); }}
        onAuthError={onAuthError}
      />

      {/* Stock overview per pickup day */}
      {overview && overview.dishes.length > 0 && (
        <div data-testid="stock-overview">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={20} className="text-[#1B6B2E]" />
            <h2 className="font-[700] text-[#0D3B4C]">Voorraadoverzicht per ophaaldag</h2>
          </div>
          <p className="text-sm text-[#0D3B4C] mb-3">Bestelde porties komen uit betaalde bestellingen. "Nog beschikbaar" is de resterende voorraad van vandaag.</p>
          <div className="overflow-x-auto border-2 border-[#E8F5E9]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#E8F5E9] text-[#0D3B4C] text-left">
                  <th className="p-3 font-[700]">Gerecht</th>
                  {overview.pickup_days.map((d) => (
                    <th key={d} className="p-3 font-[700] text-center">{d}</th>
                  ))}
                  <th className="p-3 font-[700] text-center">Totaal besteld</th>
                  <th className="p-3 font-[700] text-center">Nog beschikbaar</th>
                </tr>
              </thead>
              <tbody>
                {overview.dishes.map((d) => (
                  <tr key={d.id} className="border-t border-[#E8F5E9]" data-testid={`stock-row-${d.id}`}>
                    <td className="p-3 font-[600] text-[#0D3B4C]">{d.name}</td>
                    {overview.pickup_days.map((day) => (
                      <td key={day} className="p-3 text-center text-[#0D3B4C]">{d.ordered_by_day[day] || 0}</td>
                    ))}
                    <td className="p-3 text-center font-[700] text-[#0D3B4C]">{d.total_ordered}</td>
                    <td className={`p-3 text-center font-[700] ${d.availability <= 0 ? "text-[#B71C1C]" : "text-[#1B6B2E]"}`} data-testid={`stock-remaining-${d.id}`}>
                      {d.availability <= 0 ? "Uitverkocht" : d.availability}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category management */}
      {categories.length > 0 && (
        <CategoryManager categories={categories} busy={busy} onSave={saveCategories} onUpload={uploadImage} />
      )}

      {/* Dish list */}
      <div className="space-y-4">
        <h2 className="font-[700] text-[#0D3B4C]">Gerechten ({items.length})</h2>
        {items.length === 0 ? (
          <p className="font-[600] text-[#0D3B4C]" data-testid="no-dishes">Nog geen gerechten. Voeg er hieronder één toe.</p>
        ) : (
          items.map((it, i) => (
            <DishCard key={it.id} item={it} first={i === 0} last={i === items.length - 1} categories={categories}
              busy={busy} onSave={saveDish} onDelete={deleteDish} onMove={moveDish} onUpload={uploadImage} />
          ))
        )}
      </div>

      {/* Add new dish */}
      <div className="border-2 border-[#1B6B2E] p-5" data-testid="add-dish-form">
        <h2 className="font-[700] text-[#0D3B4C] mb-4">Nieuw gerecht toevoegen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Naam</label>
            <input value={adding.name} onChange={(e) => setAdding((p) => ({ ...p, name: e.target.value }))} data-testid="add-dish-name"
              className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Prijs (€)</label>
            <input type="number" step="0.01" min="0" value={adding.price} onChange={(e) => setAdding((p) => ({ ...p, price: e.target.value }))} data-testid="add-dish-price"
              className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Beschrijving</label>
          <textarea value={adding.desc} onChange={(e) => setAdding((p) => ({ ...p, desc: e.target.value }))} data-testid="add-dish-desc" rows={2}
            className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[400] focus:border-[#B71C1C] focus:outline-none" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 items-end">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Categorie</label>
            <select value={adding.category} onChange={(e) => setAdding((p) => ({ ...p, category: e.target.value }))} data-testid="add-dish-category"
              className="w-full border-2 border-[#7A5230] px-3 py-2 font-[700] text-[#7A5230] bg-white focus:border-[#B71C1C] focus:outline-none">
              {categories.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Standaard beschikbaarheid/dag</label>
            <input type="number" min="0" value={adding.default_availability} onChange={(e) => setAdding((p) => ({ ...p, default_availability: e.target.value }))} data-testid="add-dish-availability"
              className="w-full border-2 border-[#0D3B4C] px-3 py-2 font-[600] focus:border-[#B71C1C] focus:outline-none" />
          </div>
        </div>
        <label className="flex items-center justify-center gap-2 mt-3 bg-[#E8F5E9] text-[#0D3B4C] font-[700] text-sm py-2 cursor-pointer hover:bg-[#d4ecd7] transition-colors">
          <Upload size={15} /> {adding.image ? "Foto gekozen ✓" : "Foto kiezen (optioneel)"}
          <input type="file" accept="image/*" className="hidden" data-testid="add-dish-image-input"
            onChange={async (e) => {
              const url = await uploadImage(e.target.files?.[0]);
              if (url) setAdding((p) => ({ ...p, image: url }));
            }} />
        </label>
        <button onClick={addDish} disabled={busy} data-testid="add-dish-submit"
          className="flex items-center gap-2 mt-4 bg-[#B71C1C] text-white font-[700] uppercase tracking-wider px-6 py-3 hover:bg-[#8E1515] transition-colors disabled:opacity-40">
          <Plus size={18} /> Gerecht toevoegen
        </button>
      </div>
    </div>
  );
};
